import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import debounce from "components/Forms/utils/debounce";
import { SpinnerBlack } from "components/Spinner";
import ErrorLabel from "components/Forms/Elements/ErrorLabel";
import Translate from "helpers/Translate";

function SearchableDropDown({
  id = "searchable-dd",
  data = [],
  displayItemInList = "name",
  displayItemInTextbox = "",
  errorKey = "",
  errors = {},
  keyword,
  onSelect,
  onScroll = () => {},
  onClose = () => {},
  handleNoResult = () => {},
  placeholder,
  dataTip,
  noResultLabel = null,
  defaultValue = ""
}) {
  const [selectedItem, setSelectedItem] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Set the default value
  useEffect(() => {
    setSelectedItem(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    setIsLoading(false);
  }, [data]);

  //   Infinite scroll
  let suggestionsWrapper = useRef("");
  let suggestions = useRef("");
  let lastScrollPosition = useRef(0);
  const DEFAULT_ITEMS_PER_PAGE = 6;
  let itemsPerPage = useRef(DEFAULT_ITEMS_PER_PAGE);
  const handleScroll = useCallback(
    debounce(() => {
      if (
        suggestions.current &&
        suggestions.current.scrollTop > lastScrollPosition.current
      ) {
        setIsLoading(true);
        lastScrollPosition.current = suggestions.current.scrollTop;
        const itemsCount = itemsPerPage.current + DEFAULT_ITEMS_PER_PAGE;
        itemsPerPage.current = itemsCount;
        onScroll(itemsCount);
      }
    }, 1000),
    []
  );

  // Listen to scroll event on suggestion list
  useEffect(() => {
    if (showSuggestions) {
      if (suggestions.current.addEventListener) {
        suggestions.current.addEventListener("scroll", handleScroll, false);
      } else if (suggestions.current.attachEvent)
        suggestions.current.attachEvent("onscroll", handleScroll);
    }
  }, [showSuggestions, handleScroll]);

  // Filter suggestions
  const handleChange = useCallback(
    debounce(value => {
      setShowSuggestions(true);
      setTouched(false);
      keyword(value);
    }, 500),
    []
  );
  return (
    <>
      <div className="form-group">
        <div className="auto-suggest-input-wrapper">
          <input
            type="text"
            id={id}
            name="searchable-dd"
            autoComplete="off"
            className="form-input"
            data-tip={dataTip || ""}
            placeholder={placeholder}
            value={selectedItem}
            onChange={e => {
              setTouched(true);
              setSelectedItem(e.target.value);
              handleChange(e.target.value);
            }}
            onClick={() => {
              setShowSuggestions(!showSuggestions);
            }}
            onPaste={() =>
              setTimeout(() => {
                setShowSuggestions(false);
              }, 1000)
            }
            onKeyUp={e => {
              if (e.which === 27) {
                setShowSuggestions(false);
                onClose();
              }
            }}
          />
          <ErrorLabel errorKey={errorKey} errors={errors} />
          <span className="spinner">
            {(touched || isLoading) && <SpinnerBlack />}
          </span>
          {showSuggestions && (
            <i
              className="fas fa-times close pointer"
              onClick={() => {
                setShowSuggestions(false);
                setSelectedItem("");
                onClose();
              }}
            ></i>
          )}
        </div>
        {showSuggestions && (
          <div
            className="suggestions-outer"
            ref={suggestionsWrapper}
            style={{ zIndex: 450 }}
          >
            <div className="suggestions" id="suggestions" ref={suggestions}>
              <ul>
                {!isEmpty(data) &&
                  data.map((item, idx) => (
                    <li
                      key={idx}
                      onClick={() => {
                        if (displayItemInTextbox) {
                          setSelectedItem(item[displayItemInTextbox]);
                        } else {
                          setSelectedItem(item[displayItemInList]);
                        }
                        setShowSuggestions(false);
                        onSelect(item);
                      }}
                    >
                      {item[displayItemInList]}
                    </li>
                  ))}
                {data && data.length === 0 && (
                  <li
                    className="no-results"
                    onClick={() => {
                      setShowSuggestions(false);
                      handleNoResult(selectedItem);
                    }}
                  >
                    {<Link to="#">{noResultLabel}</Link> ||
                      Translate({ id: "no_results_to_show" })}
                  </li>
                )}
                {isLoading && <li className="center">Loading more...</li>}
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

SearchableDropDown.propTypes = {
  id: PropTypes.string,
  data: PropTypes.array,
  displayItemInList: PropTypes.string.isRequired,
  displayItemInTextbox: PropTypes.string,
  errorKey: PropTypes.string,
  errors: PropTypes.object,
  keyword: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onScroll: PropTypes.func,
  onClose: PropTypes.func,
  handleNoResult: PropTypes.func,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.any
};

export default SearchableDropDown;
